import { DocumentMapping } from "@/configuration/document-mappings/types";
import { PA_EAST_MAPPINGS } from "@/configuration/groups/pediatricassociates-east/document-mappings";
import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { Classification } from "@/src/models/constants/classifications";

/**
 * PA Central is the same customer's second eCW instance as PA East, running identical intake logic.
 * This file is a TRANSFORM over PA_EAST_MAPPINGS, not an independent source of truth: every entry is
 * East's entry with the tenant `group` restamped, plus a folder-name override for the 4 classifications
 * whose eCW folder name differs between the two instances (see FOLDER_OVERRIDES below). To change shared
 * intake behavior, edit pediatricassociates-east/document-mappings.ts — it applies here automatically.
 * If Central ever needs a real override beyond folder naming, extend toCentralMapping (or add another
 * per-classification override map alongside FOLDER_OVERRIDES) — don't fork this back into a duplicate.
 */

const FOLDER_OVERRIDES: Partial<Record<Classification, string>> = {
  [Classification.CONSENT_FOR_RECORDS_SPECIALIST]: "Referral Notes",
  [Classification.PRIOR_AUTHORIZATION_REFERRAL_RESPONSE]: "Referral Notes",
  [Classification.PROGRESS_NOTE]: "Referral Notes",
  [Classification.REFERRAL_RESPONSE]: "Referral Notes",
};

const STANDARD_DI_ADDITIONAL_WORKFLOW = PA_EAST_MAPPINGS[Classification.IMAGING_RESULTS_X_RAY]?.additionalWorkflow;

const ADDITIONAL_WORKFLOW_OVERRIDES: Partial<Record<Classification, readonly string[] | null>> = {
  [Classification.IMAGING_RESULTS_EKG]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_MRI]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_CT]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_ECHO]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_ULTRASOUND]: STANDARD_DI_ADDITIONAL_WORKFLOW,
  [Classification.IMAGING_RESULTS_STRESS_TEST]: STANDARD_DI_ADDITIONAL_WORKFLOW,
};

function toCentralMapping(classification: Classification, eastMapping: DocumentMapping): DocumentMapping {
  const folder = FOLDER_OVERRIDES[classification];
  const additionalWorkflow = ADDITIONAL_WORKFLOW_OVERRIDES[classification];

  return {
    ...eastMapping,
    group: ConfigSubdomain.PA_CENTRAL_REGION,
    ...(folder ? { folder, folderMapping: folder } : {}),
    ...(additionalWorkflow ? { additionalWorkflow } : {}),
  };
}

export const PA_CENTRAL_MAPPINGS: Partial<Record<Classification, DocumentMapping>> = (() => {
  const mappings: Partial<Record<Classification, DocumentMapping>> = {};

  for (const classification of Object.keys(PA_EAST_MAPPINGS) as Classification[]) {
    const eastMapping = PA_EAST_MAPPINGS[classification];

    if (eastMapping) mappings[classification] = toCentralMapping(classification, eastMapping);
  }

  return mappings;
})();
